// How many Québec death certificates the family bought, for the DEC row in
// the case sidebar. The agent filing the DEC orders exactly this many, so it
// must count only what was actually paid for.
//
// Families often buy the package plus a certificate or two, then come back for
// more; those later purchases are separate add-on contracts holding only the
// extra certificate lines. A paid add-on adds to the order. An unpaid one
// (drafted, sent, never paid) must not — case 874 showed ×3 when the family
// had paid for 2 (T-121).
//
// "Paid" = the contract has an sb_payments row with status paid/completed and
// a positive amount (refunds are negative "succeeded" rows). Signed-ness is not
// a signal: contracts.signed_at is never written.
//
// A family that already paid for a standard certificate can switch it to
// accelerated processing. That's billed as the price difference on its own
// "upgrade" item, which is not a certificate: each upgrade turns one standard
// into one accelerated without adding to the total ordered.
//
// When NO contract on the case is paid (e-transfer / cheque cases have no
// sb_payments row), we still show what the contracts hold, flagged as unpaid,
// rather than hiding the row: staff need the number to file, and the flag
// tells them to confirm payment first.

// QuickBooks item ids for the two Quebec death certificate products. Contracts
// written before the items were wired up carry no item id, so we also match on
// the line description the same way the contract PDF template does.
export const DC_STANDARD_ITEM = '15';
export const DC_EXPEDITED_ITEM = '1010000051';
// "Quebec Death Certificate - Upgrade to Expedited": the price difference only.
export const DC_UPGRADE_ITEM = '37';

export type ContractLine = {
  item?: string | number | null;
  description?: string | null;
  quantity?: number | string | null;
};

export type ContractForCount = {
  id: string | number;
  lines?: ContractLine[] | null;
};

export type PaymentForCount = {
  contract_id?: string | number | null;
  payment_status?: string | null;
  amount_total?: number | null;
};

export type ContractCertCount = {
  contractId: string | number;
  standard: number;
  expedited: number;
  /** Standard certificates switched to accelerated processing. */
  upgraded: number;
  paid: boolean;
};

export type DeathCertificateCounts = {
  /** What the sidebar shows (paid contracts only, or everything when nothing is paid). */
  standard: number;
  expedited: number;
  /** Certificates on unpaid contracts that were left out of the counts above. */
  excludedUnpaid: number;
  /** Upgrades on unpaid contracts that were left out of the counts above. */
  excludedUnpaidUpgrades: number;
  /** True when no contract on the case is paid: the counts are unconfirmed. */
  unpaidOnly: boolean;
  /** One entry per contract carrying certificate lines, in input order. */
  perContract: ContractCertCount[];
};

const lineName = (line: ContractLine) => (line.description ?? '').toLowerCase();

export const isDeathCertLine = (line: ContractLine) => {
  const item = line.item != null ? String(line.item) : '';
  if (item === DC_STANDARD_ITEM || item === DC_EXPEDITED_ITEM) return true;
  if (item) return false;
  const name = lineName(line);
  return (
    name.includes('death certificate') || name.includes('certificat de décès')
  );
};

export const isExpeditedLine = (line: ContractLine) => {
  const item = line.item != null ? String(line.item) : '';
  if (item === DC_EXPEDITED_ITEM) return true;
  if (item === DC_STANDARD_ITEM) return false;
  const name = lineName(line);
  return (
    name.includes('expedited') ||
    name.includes('accéléré') ||
    name.includes('rapide')
  );
};

export const isUpgradeLine = (line: ContractLine) =>
  line.item != null && String(line.item) === DC_UPGRADE_ITEM;

const PAID_STATUSES = new Set(['paid', 'completed']);

export const isSuccessfulPayment = (p: PaymentForCount) =>
  PAID_STATUSES.has((p.payment_status ?? '').toLowerCase()) &&
  Number(p.amount_total ?? 0) > 0;

export function countDeathCertificates(
  contracts: ContractForCount[],
  payments: PaymentForCount[]
): DeathCertificateCounts {
  const paidIds = new Set(
    payments.filter(isSuccessfulPayment).map((p) => String(p.contract_id))
  );

  const perContract: ContractCertCount[] = [];
  for (const c of contracts) {
    const lines = Array.isArray(c.lines) ? c.lines : [];
    let standard = 0;
    let expedited = 0;
    let upgraded = 0;
    for (const line of lines) {
      const upgrade = isUpgradeLine(line);
      if (!upgrade && !isDeathCertLine(line)) continue;
      const qty = Number(line.quantity ?? 1);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      if (upgrade) upgraded += qty;
      else if (isExpeditedLine(line)) expedited += qty;
      else standard += qty;
    }
    if (standard + expedited + upgraded === 0) continue;
    perContract.push({
      contractId: c.id,
      standard,
      expedited,
      upgraded,
      paid: paidIds.has(String(c.id)),
    });
  }

  const anyPaidContract = contracts.some((c) => paidIds.has(String(c.id)));
  const counted = anyPaidContract
    ? perContract.filter((c) => c.paid)
    : perContract;
  const excluded = anyPaidContract ? perContract.filter((c) => !c.paid) : [];

  const sum = (cs: ContractCertCount[], key: 'standard' | 'expedited' | 'upgraded') =>
    cs.reduce((s, c) => s + c[key], 0);
  // An upgrade can only switch a standard certificate the family has.
  const standard = sum(counted, 'standard');
  const upgraded = Math.min(sum(counted, 'upgraded'), standard);

  return {
    standard: standard - upgraded,
    expedited: sum(counted, 'expedited') + upgraded,
    excludedUnpaid: sum(excluded, 'standard') + sum(excluded, 'expedited'),
    excludedUnpaidUpgrades: sum(excluded, 'upgraded'),
    unpaidOnly: !anyPaidContract && perContract.length > 0,
    perContract,
  };
}

/**
 * Tooltip text: "2 from contract #747 (paid) · 1 from contract #756 (unpaid, not counted)".
 * An upgrade-only contract reads "1 upgraded to accelerated on contract #760 (paid)".
 */
export function describeBreakdown(counts: DeathCertificateCounts): string {
  return counts.perContract
    .map((c) => {
      const n = c.standard + c.expedited;
      const status = c.paid
        ? 'paid'
        : counts.unpaidOnly
        ? 'no payment on file'
        : 'unpaid, not counted';
      const what =
        c.upgraded === 0
          ? `${n}`
          : n === 0
          ? `${c.upgraded} upgraded to accelerated`
          : `${n} + ${c.upgraded} upgraded to accelerated`;
      return `${what} ${n > 0 ? 'from' : 'on'} contract #${c.contractId} (${status})`;
    })
    .join(' · ');
}
